import TileActionResult from 'app/src/engine/action-results/TileActionResult';
import MovableItem, { MovableItemOrientation } from 'app/src/engine/movable-items/MovableItem';
import TileInteraction from 'app/src/engine/tile-interactions/TileInteraction';
import Tile from 'app/src/engine/tiles/Tile';

export type TileProvider = {
	getTile(description: { [key: string]: any }): Tile;
};

export type TileNodeProvider = {
	providesNode(tile: Tile): boolean;
	getNode(tile: Tile): React.ReactElement;
};

export type MovableItemProvider = {
	getMovableItem(description: { [key: string]: any }): MovableItem;
};

export type MovableItemNodeProvider = {
	providesNode(movableItem: MovableItem): boolean;
	getNode(movableItem: MovableItem, orientation: MovableItemOrientation): React.ReactElement;
};

export type TileData = { [key: string]: any };
export type MovableItemData = { [key: string]: any };

export default class GamePlugin {
	// Get a list of Tiles that this plugin provides. When the tile grid is
	// being initialized, it'll ask all plugins which Tiles they create, and
	// later use those corresponding plugins to build Tiles.
	//
	// Subclasses should override if they provide any Tiles.
	public getListOfProvidedTiles(): string[] {
		return [];
	}

	// Based on Tile data, generate a Tile.
	//
	// Subclasses should override if they provide any Tiles.
	public getTile(_data: TileData): Tile {
		throw new Error('Unsupported Tile.');
	}

	// If the editor chooses to support this plugin, and if this plugin displays
	// tiles, this list of tiles will be used in the side panel.
	//
	// Subclasses should override if they provide any Tiles.
	public getCanonicalTiles(): Tile[] {
		return [];
	}

	// Given a Tile, is this plugin capable of generating a corresponding TileNode?
	//
	// Subclasses should override if they provide any TilesNodes.
	public providesNodeForTile(_tile: Tile): boolean {
		return false;
	}

	// Based on a Tile, generate a TileNode.
	//
	// Subclasses should override if they provide any TilesNodes.
	public getTileNode(
		_tile: Tile,
		_row: number,
		_col: number,
		_actionResults?: TileActionResult[] | null,
		_completeAnimation?: () => void,
	): React.ReactElement {
		throw new Error('Unsupported Tile node.');
	}

	// Get a list of MovableItems that this plugin provides. When the game
	// state is being initialized, it'll ask all plugins which MovableItems
	// they create, and later use those corresponding plugins to build
	// MovableItems.
	//
	// Subclasses should override if they provide any MovableItems.
	public getListOfProvidedMovableItems(): string[] {
		return [];
	}

	// Based on MovableItem data, generate a MovableItems.
	//
	// Subclasses should override if they provide any MovableItems.
	public getMovableItem(_data: MovableItemData): MovableItem {
		throw new Error('Unsupported MovableItem.');
	}

	// Given a MovableItem, is this plugin capable of generating a corresponding
	// MovableItemNode?
	//
	// Subclasses should override if they provide any MovableItemNodes.
	public providesNodeForMovableItem(_movableItem: MovableItem): boolean {
		return false;
	}

	// Based on a MovableItem, generate a MovableItemNode.
	//
	// Subclasses should override if they provide any MovableItemNodes.
	public getMovableItemNode(_movableItem: MovableItem): React.ReactElement {
		throw new Error('Unsupported MovableItem node.');
	}

	// Get a list of tile interaction plugins that this plugin provides.
	//
	// Subclasses should override if they provide any TileInteractions.
	public getTileInteractions(): TileInteraction[] {
		return [];
	}
}
